<script setup lang="ts">
    import { useGetInteractions } from '@/js/hooks/interaction';
    import { IRowCell } from '@/js/services/api/interaction/types';
    import { computed, ref, watch } from 'vue';
    import LoadingDots from '../../LoadingDots.vue';
    import InteractionSummary from '../summary/InteractionSummary.vue';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '../../EchoButton.vue';
    import TranscriptionChat from '../transcription/TranscriptionChat.vue';
    import SvgIcon from '../../SvgIcon.vue';

    const props = defineProps<{
        data: IRowCell;
        containerRef: HTMLElement | null;
    }>();

    const req = computed(() => [props.data.id]);

    const { data: interactions, isLoading } = useGetInteractions(req);

    const interaction = computed(() => {
        return interactions.value?.interactions[0];
    });

    const noData = computed(() => {
        return (
            interaction.value &&
            interaction.value.transcription.length === 0 &&
            !interaction.value.summary
        );
    });

    const width = ref(0);

    const currentTime = ref(0);

    watch(
        () => props.containerRef,
        (newVal) => {
            if (newVal) {
                width.value = newVal.clientWidth - 24;
            }
        },
        { immediate: true }
    );

    const actionButtons = computed(() => [
        { label: __('open'), variant: 'primary' as const, route: `/interaction/${props.data.id}` },
        {
            label: __('interactions-coach-now'),
            variant: 'secondary' as const,
            route: `/quality-assurance/${props.data.id}`,
        },
    ]);
</script>
<template>
    <div class="flex relative">
        <div
            :style="{ width: width + 'px' }"
            class="w-full sticky top-0 left-3 border-1 border-gray-lightest rounded-lg pt-6.5 px-6 pb-7.5"
        >
            <div v-if="isLoading">
                <LoadingDots />
            </div>
            <div v-else-if="noData" class="flex flex-col items-center gap-3">
                <div class="flex w-full justify-end gap-2.5">
                    <EchoButton
                        v-for="button in actionButtons"
                        :key="button.label"
                        :variant="button.variant"
                        size="small"
                        :route="button.route"
                    >
                        {{ button.label }}
                    </EchoButton>
                </div>
                <SvgIcon
                    name="sentiment-neutraltonegative-gray.svg"
                    class="stroke-transparent"
                    size="size-11"
                />
                <p class="text-body">
                    {{ __('interactions-table-empty-message') }}
                </p>
            </div>
            <div v-else>
                <div class="flex gap-10">
                    <div class="flex-5">
                        <div class="flex items-center justify-between pr-3 mb-5">
                            <div class="flex">
                                <h2 class="heading-md">
                                    {{ __('transcription') }}
                                </h2>
                            </div>
                            <div class="flex gap-2.5">
                                <EchoButton
                                    v-for="button in actionButtons"
                                    :key="button.label"
                                    :variant="button.variant"
                                    size="small"
                                    :route="button.route"
                                >
                                    {{ button.label }}
                                </EchoButton>
                            </div>
                        </div>
                        <div class="-mb-7 -mr-5">
                            <TranscriptionChat
                                v-if="interaction && interaction.transcription"
                                :interaction="interaction"
                                :current-time="currentTime"
                            />
                        </div>
                    </div>
                    <div class="flex-3">
                        <InteractionSummary
                            v-if="interaction && interaction.summary"
                            :summary="interaction.summary"
                            compact-heading
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
